import { InputOtp, Spinner } from '@heroui/react';
import { FirebaseError } from 'firebase/app';
import { Eye, EyeOff } from 'lucide-react';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useHeroToast } from '@/hooks';
import { useUserState } from '@/state';
import { useLocaleNavigate } from '@/utils/locale';

import {
  AuthCard,
  Content,
  Form,
  FormSection,
  LoginHeader,
  MainContainer,
  PrimaryButton,
  Title,
} from '../auth.styled';
import { AuthFooterBar } from '../components/AuthFooterBar';

interface InvitePinForm {
  pin: string;
}

// Wydzielony komponent dla toggle widoczności
const VisibilityToggle: FC<{ visible: boolean; onToggle: () => void }> = ({ visible, onToggle }) => {
  const Icon = visible ? Eye : EyeOff;
  return <Icon className="cursor-pointer" strokeWidth={1.5} onClick={onToggle} />;
};

export const InvitePin: FC = () => {
  const { t } = useTranslation();
  const { register, handleSubmit } = useForm<InvitePinForm>();
  const [isLoading, setIsLoading] = useState(false);
  const [isPinVisible, setIsPinVisible] = useState(false);
  const { showError } = useHeroToast();
  const { signInWithEmail } = useUserState();
  const navigate = useLocaleNavigate();
  const [searchParams] = useSearchParams();

  const email = searchParams.get('email');

  const getFirebaseErrorMessage = (error: unknown): string => {
    if (error instanceof FirebaseError) {
      const errorKey = `firebaseErrors.${error.code}`;
      const translated = t(errorKey);
      if (translated !== errorKey) {
        return translated;
      }

      const messageKey = `firebaseErrors.${error.message}`;
      const messageTranslated = t(messageKey);
      if (messageTranslated !== messageKey) {
        return messageTranslated;
      }
    }
    return t('firebaseErrors.default');
  };

  const onSubmit = async ({ pin }: InvitePinForm): Promise<void> => {
    if (!email) {
      showError(t('firebaseErrors.auth/invalid-email'));
      return;
    }

    if (!pin || pin.length !== 6) {
      showError(t('firebaseErrors.auth/invalid-credential'));
      return;
    }

    setIsLoading(true);

    try {
      await signInWithEmail(email, pin);
      navigate('/');
    } catch (error) {
      showError(getFirebaseErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainContainer>
      <AuthCard $centered>
        <FormSection>
          <LoginHeader>
            s
          </LoginHeader>

          <Title>{t('auth.invitePin.title')}</Title>
          <Content>
            {t('auth.invitePin.subtitle')}
          </Content>

          <Form onSubmit={handleSubmit(onSubmit)}>
            <input type="hidden" value={email || ''} {...register('pin')} />

            <InputOtp
              length={6}
              radius="md"
              size="lg"
              type={isPinVisible ? 'text' : 'password'}
              variant="bordered"
            />
            <VisibilityToggle
              visible={isPinVisible}
              onToggle={() => setIsPinVisible((v) => !v)}
            />

            <PrimaryButton type="submit" disabled={isLoading}>
              {isLoading && <Spinner size="sm" variant="spinner" classNames={{ circle1: 'border-b-white', circle2: 'border-b-white' }} />}
              {t('common.submit')}
            </PrimaryButton>
          </Form>
        </FormSection>
      </AuthCard>

      <AuthFooterBar />
    </MainContainer>
  );
};
